import { resolveEnemyLevel } from '../sim/enemy-level.js';
import { resolveSourceProfile } from '../sim/source-profile.js';
import { WEAPON_PRIMARY_PROFILE_V0 } from '../sim/weapon-primary-profile.js';
import { aggregateEffectiveStats, componentMultiplier, criticalDamageBonus } from '../sim/stats.js';
import { primaryStatV0DerivedStats } from './primary-stat-v0.js';
import { combatSourceAtLevel, completePrimaryStats, primaryAllocationForShape } from './weapon-progression-v0.js';
import { WEAPON_ENHANCE_TOKEN_LEVEL_INTERVAL, weaponEnhanceMultiplier, weaponEnhanceTokensPaidAt } from './weapon-enhance.js';

/**
 * The field monster power rule's reference: the SWORD reference build's basic attack and the HP it prices. It lives below
 * the game layer so a combat host that never loads the game (the standalone world, the offline simulator) reads the same
 * reference monster; `game/monster-power-policy.ts` re-uses it.
 */

/** Non-critical reference hits a level-1 monster of each archetype takes. */
export const FIELD_MONSTER_H0_MULTIPLE: Readonly<Record<string, number>> = Object.freeze({ swarm: 1.5, melee: 2.5, ranged: 2.5, elite: 7.5, boss: 30 });

/** PROVISIONAL: the reference build has spent every 무기 강화권 it has earned by `level` (the first is paid at Lv30). */
export function referenceWeaponEnhanceStep(level: number): number {
  return weaponEnhanceTokensPaidAt(Math.floor(level / WEAPON_ENHANCE_TOKEN_LEVEL_INTERVAL));
}

/**
 * Basic attack of the SWORD reference build at `level`: the 3:1:1 allocation at 5 points per
 * level and the weapon step above, resolved through the common source profile the live basic attack uses. `hit` is one
 * non-critical hit; `damagePerS` is the expected damage per second, with the build's critical
 * chance and critical damage and its attack cycle.
 */
function referenceSwordBasic(level: number): { readonly hit: number; readonly damagePerS: number } {
  resolveEnemyLevel(level);
  const allocation = primaryAllocationForShape('BALANCED', [3 * level, level, level]);
  const stats = completePrimaryStats(allocation);
  const base = combatSourceAtLevel('SWORD', 20);
  const source = { ...base, baseDamage: base.baseDamage * weaponEnhanceMultiplier(referenceWeaponEnhanceStep(level)) };
  const derived = primaryStatV0DerivedStats(level, allocation, source.family);
  const effective = aggregateEffectiveStats({ globalDamage: 0, critChance: derived.critChance, critDamage: derived.critDamage, critDamageByClass: derived.critDamageByClass });
  // A weapon basic attack resolves through the common source profile alone (`resolveBasicAttackSource` adds modifiers only for a catalyst's).
  const resolved = resolveSourceProfile(source, stats, WEAPON_PRIMARY_PROFILE_V0);
  const hit = resolved.hitDamage * componentMultiplier(effective, 'PHYSICAL', 'NONE');
  return { hit, damagePerS: hit * (1 + effective.critChance * criticalDamageBonus(effective, 'PHYSICAL')) / resolved.cycleS };
}

/** H0(level): one non-critical basic hit of the SWORD reference build. */
export function referenceSwordBasicHit(level: number): number {
  return referenceSwordBasic(level).hit;
}

/** Expected damage per second of the SWORD reference build's basic attack, critical hits and attack speed included. */
export function referenceSwordBasicDamagePerS(level: number): number {
  return referenceSwordBasic(level).damagePerS;
}

/** The HP the rule gives an archetype that takes `h0Multiple` level-1 reference hits, at `level`. */
export function fieldMonsterHpAtLevel(h0Multiple: number, level: number): number {
  return h0Multiple * referenceSwordBasicHit(1) * referenceSwordBasicDamagePerS(level) / referenceSwordBasicDamagePerS(1);
}

const referenceMaxHpByLevel = new Map<number, number>();
/** The maximum HP of the monster-level model's reference monster (the melee archetype) at `level`: what an HP-fraction threshold is a fraction of, whatever the carrier's own HP. */
export function fieldMonsterReferenceMaxHp(level: number): number {
  let hp = referenceMaxHpByLevel.get(level);
  if (hp === undefined) { hp = fieldMonsterHpAtLevel(FIELD_MONSTER_H0_MULTIPLE.melee!, level); referenceMaxHpByLevel.set(level, hp); }
  return hp;
}
